import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Logo } from "@/components/Logo";
import { Elf } from "@/components/Elf";
import { Lantern } from "@/components/Lantern";
import { useAuth } from "@/context/AuthContext";
import { api, formatApiError } from "@/lib/api";

export const SiteFooter = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post("/newsletter/subscribe", { email: email.trim() });
      toast.success(data.already ? "Sei già iscritto alla newsletter!" : "Iscrizione confermata! A presto 🎄");
      setEmail("");
    } catch (err) {
      toast.error(formatApiError(err, "Iscrizione non riuscita."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="relative z-10 mt-10" data-testid="site-footer">
      {/* snow drifts with lanterns and a walking elf */}
      <div aria-hidden className="relative -mb-1">
        <Lantern
          className="absolute bottom-10 left-6 z-10 w-8 sm:bottom-14 sm:left-20 sm:w-11"
          data-testid="lantern-footer-1"
        />
        <Lantern
          className="absolute bottom-6 left-1/3 z-10 w-6 sm:bottom-10 sm:w-8"
          data-testid="lantern-footer-2"
        />
        <Lantern
          className="absolute bottom-12 right-8 z-10 w-9 sm:bottom-16 sm:right-1/4 sm:w-12"
          data-testid="lantern-footer-3"
        />
        <motion.div
          data-testid="elf-footer-walk"
          className="absolute bottom-3 z-10 w-16 sm:bottom-6 sm:w-20"
          animate={{ left: ["4%", "62%", "4%"], scaleX: [1, 1, -1, -1, 1] }}
          transition={{ duration: 24, repeat: Infinity, ease: "linear", times: [0, 0.48, 0.52, 0.98, 1] }}
        >
          <Elf variant="walk" />
        </motion.div>
        <svg viewBox="0 0 1440 150" preserveAspectRatio="none" className="block h-24 w-full sm:h-36">
          <path
            d="M0,90 C240,35 430,120 720,75 C1010,30 1200,100 1440,55 L1440,150 L0,150 Z"
            fill="#DCE7F0"
            opacity="0.35"
          />
          <path
            d="M0,110 C260,60 480,132 760,95 C1040,58 1240,120 1440,82 L1440,150 L0,150 Z"
            fill="#EEF3F8"
            opacity="0.7"
          />
          <path
            d="M0,126 C300,88 540,144 820,110 C1100,80 1280,132 1440,104 L1440,150 L0,150 Z"
            fill="#FAFCFE"
          />
        </svg>
      </div>

      {/* footer body on the snow */}
      <div className="bg-[#FAFCFE] text-[#12352A]">
        <div className="mx-auto grid max-w-7xl gap-10 px-5 pb-8 pt-4 sm:grid-cols-2 sm:px-8 lg:grid-cols-5">
          <div>
            <div className="flex items-center gap-2.5">
              <Logo className="h-9 w-9" />
              <span className="font-display text-lg font-bold">Avvento d'Oro</span>
            </div>
            <p className="mt-3 text-sm leading-relaxed text-[#12352A]/70">
              Il Calendario dell'Avvento digitale: 24 giorni, 24 sorprese dai migliori brand d'Italia.
            </p>
          </div>

          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#8B1E2B]">Esplora</h4>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li><Link to="/" data-testid="footer-link-home" className="transition-colors hover:text-[#8B1E2B]">Home</Link></li>
              <li>
                <button
                  type="button"
                  data-testid="footer-link-buy"
                  onClick={() => navigate(user ? "/dashboard" : "/registrati")}
                  className="transition-colors hover:text-[#8B1E2B]"
                >
                  Compra il calendario
                </button>
              </li>
              <li>
                <button
                  type="button"
                  data-testid="footer-link-gift"
                  onClick={() => navigate(user ? "/dashboard" : "/registrati")}
                  className="transition-colors hover:text-[#8B1E2B]"
                >
                  Regalalo
                </button>
              </li>
              <li><Link to="/contattaci" data-testid="footer-link-contatti" className="transition-colors hover:text-[#8B1E2B]">Contattaci</Link></li>
              <li><Link to="/diventa-partner" data-testid="footer-link-partner" className="transition-colors hover:text-[#8B1E2B]">Diventa partner</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#8B1E2B]">Account</h4>
            <ul className="mt-4 space-y-2.5 text-sm">
              {user ? (
                <li>
                  <Link to={user.role === "admin" ? "/admin" : "/dashboard"} data-testid="footer-link-area" className="transition-colors hover:text-[#8B1E2B]">
                    {user.role === "admin" ? "Pannello admin" : "Area personale"}
                  </Link>
                </li>
              ) : (
                <>
                  <li><Link to="/accedi" data-testid="footer-link-login" className="transition-colors hover:text-[#8B1E2B]">Accedi</Link></li>
                  <li><Link to="/registrati" data-testid="footer-link-register" className="transition-colors hover:text-[#8B1E2B]">Registrati</Link></li>
                </>
              )}
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#8B1E2B]">Note legali</h4>
            <ul className="mt-4 space-y-2.5 text-sm">
              <li><Link to="/termini-e-condizioni" data-testid="footer-link-termini" className="transition-colors hover:text-[#8B1E2B]">Termini e Condizioni</Link></li>
              <li><Link to="/privacy-policy" data-testid="footer-link-privacy" className="transition-colors hover:text-[#8B1E2B]">Informativa Privacy</Link></li>
              <li><Link to="/cookie-policy" data-testid="footer-link-cookie" className="transition-colors hover:text-[#8B1E2B]">Cookie Policy</Link></li>
            </ul>
          </div>

          <div>
            <h4 className="text-xs font-bold uppercase tracking-[0.2em] text-[#8B1E2B]">Newsletter</h4>
            <p className="mt-4 text-sm text-[#12352A]/70">
              Anteprime sulle sorprese e sui partner, una volta ogni tanto.
            </p>
            <form onSubmit={subscribe} className="mt-3" data-testid="newsletter-form">
              <div className="flex gap-2">
                <input
                  type="email"
                  required
                  data-testid="newsletter-email-input"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="La tua email"
                  className="w-full rounded-xl border border-[#12352A]/20 bg-white px-4 py-2.5 text-sm text-[#12352A] placeholder:text-[#12352A]/40 outline-none focus:border-[#8B1E2B]/60"
                />
                <button
                  type="submit"
                  data-testid="newsletter-subscribe-button"
                  disabled={busy}
                  aria-label="Iscriviti"
                  className="shrink-0 rounded-xl bg-gradient-to-r from-[#8B1E2B] to-[#5C111C] px-4 py-2.5 text-amber-100 transition-transform hover:scale-105 disabled:opacity-60"
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
              <label className="mt-2.5 flex items-start gap-2 text-[11px] leading-snug text-[#12352A]/70" data-testid="newsletter-consent-label">
                <input
                  type="checkbox"
                  required
                  data-testid="newsletter-consent-checkbox"
                  className="mt-0.5 h-3.5 w-3.5 shrink-0 accent-[#8B1E2B]"
                />
                <span>
                  Ho letto l'<Link to="/privacy-policy" className="font-semibold underline">Informativa Privacy</Link> e
                  acconsento a ricevere la newsletter.
                </span>
              </label>
            </form>
          </div>
        </div>

        <div className="border-t border-[#12352A]/10">
          <p className="mx-auto max-w-7xl px-5 py-5 text-center text-xs text-[#12352A]/55 sm:px-8">
            © 2026 Avvento d'Oro — Un regalo nuovo ogni giorno, dal 1 al 24 dicembre.
          </p>
        </div>
      </div>
    </footer>
  );
};
